import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, Mail, Phone, ExternalLink, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bc_admin_token") || ""}` });

const STATUSES = ["New", "Reviewing", "Contacted", "Onboarded", "Not a fit", "Follow up"];
const STATUS_STYLES = {
  "New": "bg-red-900/25 border-red-700/40 text-red-300",
  "Reviewing": "bg-amber-900/25 border-amber-700/40 text-amber-300",
  "Contacted": "bg-blue-900/25 border-blue-700/40 text-blue-300",
  "Onboarded": "bg-emerald-900/30 border-emerald-700/50 text-emerald-300",
  "Not a fit": "bg-zinc-800/50 border-zinc-700 text-zinc-400",
  "Follow up": "bg-purple-900/25 border-purple-700/40 text-purple-300",
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function PersonalBrandingAdmin({ onAuthFail }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/personal-branding`, { headers: authHeaders() });
      setLeads(data);
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail?.();
      else toast.error("Could not load leads");
    } finally { setLoading(false); }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const openLead = (l) => { setSelected(l); setRemarks(l.remarks || ""); };

  const updateStatus = async (lead_id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/personal-branding/${lead_id}`, { status }, { headers: authHeaders() });
      setLeads((prev) => prev.map((x) => (x.id === lead_id ? data : x)));
      if (selected?.id === lead_id) setSelected(data);
      toast.success("Status updated");
    } catch { toast.error("Could not update"); }
  };

  const saveRemarks = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/personal-branding/${selected.id}`, { remarks }, { headers: authHeaders() });
      setLeads((prev) => prev.map((x) => (x.id === selected.id ? data : x)));
      setSelected(data);
      toast.success("Remarks saved");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const removeLead = async (lead_id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/personal-branding/${lead_id}`, { headers: authHeaders() });
      setLeads((prev) => prev.filter((x) => x.id !== lead_id));
      if (selected?.id === lead_id) setSelected(null);
      toast.success("Deleted");
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="pb-admin-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Personal Branding intake</div>
          <div className="text-sm text-zinc-400 mt-1">{leads.length} lead{leads.length === 1 ? "" : "s"}</div>
        </div>
        <button onClick={load} className="btn-ghost text-xs" data-testid="pb-admin-refresh">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-[1fr_400px] gap-4">
        <div className="luxury-card p-0 overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto">
            {leads.length === 0 && !loading && (
              <div className="p-8 text-center text-xs text-zinc-500">No Personal Branding applications yet.</div>
            )}
            {leads.map((l) => (
              <button
                key={l.id} onClick={() => openLead(l)}
                data-testid={`pb-lead-${l.id}`}
                className={`w-full text-left px-4 py-4 border-b border-white/5 hover:bg-white/[0.03] ${selected?.id === l.id ? "bg-red-500/5" : ""}`}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-sm text-white font-medium truncate">{l.name}</div>
                  <span className={`text-[10px] px-2 py-0.5 border ${STATUS_STYLES[l.status] || "border-white/10 text-zinc-400"}`} style={{ borderRadius: 0 }}>
                    {l.status || "—"}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500 space-x-3">
                  <span>{l.email}</span>
                  {l.phone && <span>· {l.phone}</span>}
                </div>
                <div className="text-[11px] text-zinc-600 mt-1">
                  Hours: {l.hours_per_day || "—"} · Budget OK: {l.budget_ok ? "Yes" : (l.budget_ok === false ? "No" : "—")} · {fmt(l.created_at)}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="luxury-card p-5 min-h-[70vh]">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-xs text-zinc-500 py-16">
              Select a lead to see the full application.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <div className="font-serif-display text-2xl">{selected.name}</div>
                  <button onClick={() => removeLead(selected.id)} className="text-zinc-500 hover:text-red-400" title="Delete" data-testid="pb-delete-btn">
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">{fmt(selected.created_at)}</div>
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <a href={`mailto:${selected.email}`} className="btn-ghost text-xs py-1.5 px-2.5"><Mail size={12} /> {selected.email}</a>
                {selected.phone && <a href={`tel:${selected.phone}`} className="btn-ghost text-xs py-1.5 px-2.5"><Phone size={12} /> {selected.phone}</a>}
              </div>

              <div className="text-xs space-y-3 pt-2 border-t border-white/5">
                <Field label="Handles / Links" value={selected.handles} isLink />
                <Field label="Goal" value={selected.goal} />
                <Field label="Hours per day" value={selected.hours_per_day} />
                <Field label="₹75k Budget OK" value={selected.budget_ok === null ? "—" : (selected.budget_ok ? "Yes" : "No")} valueClass={selected.budget_ok ? "text-emerald-400" : (selected.budget_ok === false ? "text-red-400" : "")} />
              </div>

              <div className="pt-3 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Status</div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map((s) => (
                    <button
                      key={s} onClick={() => updateStatus(selected.id, s)}
                      data-testid={`pb-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`text-[10px] px-2 py-1 border ${selected.status === s ? STATUS_STYLES[s] : "border-white/10 text-zinc-400 hover:text-white"}`}
                      style={{ borderRadius: 0 }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Remarks</div>
                <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} className="input-luxury resize-none text-sm" data-testid="pb-remarks-input" />
                <button onClick={saveRemarks} disabled={saving} className="btn-primary text-xs mt-2 py-2 px-3 disabled:opacity-40" data-testid="pb-save-remarks">
                  <Save size={12} /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, isLink, valueClass }) {
  if (!value || value === "—") return (
    <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">{label}</div><div className="text-zinc-500">—</div></div>
  );
  const isUrl = isLink && /https?:\/\//i.test(value);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">{label}</div>
      {isLink && isUrl ? (
        <div className={`text-zinc-200 whitespace-pre-wrap break-words ${valueClass || ""}`}>
          {value.split(/\s+/).map((tok, i) => (/^https?:\/\//i.test(tok) ? (
            <a key={i} href={tok} target="_blank" rel="noreferrer" className="text-red-400 hover:underline inline-flex items-center gap-1">{tok}<ExternalLink size={10} /></a>
          ) : (<span key={i}>{tok} </span>)))}
        </div>
      ) : (
        <div className={`text-zinc-200 whitespace-pre-wrap ${valueClass || ""}`}>{value}</div>
      )}
    </div>
  );
}
